import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { PART_KIND_LABELS, type TenderOutlineNode, type TenderPartKind } from '../../types/tender';

/** 提交给后端的目录节点结构 */
export interface TenderOutlineEditPayload {
  title: string;
  description: string | null;
  word_count_range: string | null;
  part_kind: TenderPartKind;
  section: string;
  stable_id: string | null;
  children: TenderOutlineEditPayload[];
}

interface EditNode {
  key: string;
  stable_id: string | null;
  title: string;
  description: string;
  word_count_range: string;
  part_kind: TenderPartKind;
  section: string;
  children: EditNode[];
}

let keySeq = 0;
const nextKey = () => `tender-node-${(keySeq += 1)}`;

const makeNode = (partial?: Partial<EditNode>): EditNode => ({
  key: nextKey(),
  stable_id: null,
  title: '',
  description: '',
  word_count_range: '',
  part_kind: 'default',
  section: 'tender_main',
  children: [],
  ...partial,
});

const buildTree = (nodes: TenderOutlineNode[]): EditNode[] => {
  const map = new Map<string, EditNode>();
  nodes.forEach((node) => {
    map.set(node.node_id, {
      key: nextKey(),
      stable_id: node.stable_id || null,
      title: node.title,
      description: node.description || '',
      word_count_range: node.word_count_range || '',
      part_kind: (node.part_kind || 'default') as TenderPartKind,
      section: node.section || 'tender_main',
      children: [],
    });
  });
  const roots: EditNode[] = [];
  nodes.forEach((node) => {
    const current = map.get(node.node_id);
    if (!current) {
      return;
    }
    const parent = node.parent_node_id ? map.get(node.parent_node_id) : undefined;
    if (parent) {
      parent.children.push(current);
    } else {
      roots.push(current);
    }
  });
  return roots;
};

const cloneTree = (nodes: EditNode[]): EditNode[] =>
  nodes.map((node) => ({ ...node, children: cloneTree(node.children) }));

interface Location {
  list: EditNode[];
  index: number;
  parent: EditNode | null;
}

const locate = (nodes: EditNode[], key: string, parent: EditNode | null = null): Location | null => {
  const index = nodes.findIndex((node) => node.key === key);
  if (index >= 0) {
    return { list: nodes, index, parent };
  }
  for (const node of nodes) {
    const found = locate(node.children, key, node);
    if (found) {
      return found;
    }
  }
  return null;
};

const countNodes = (nodes: EditNode[]): number =>
  nodes.reduce((total, node) => total + 1 + countNodes(node.children), 0);

const toPayload = (nodes: EditNode[]): TenderOutlineEditPayload[] =>
  nodes
    .filter((node) => node.title.trim())
    .map((node) => ({
      title: node.title.trim(),
      description: node.description.trim() || null,
      word_count_range: node.word_count_range.trim() || null,
      part_kind: node.part_kind,
      // 格式附件统一归入 tender_annex，其余归正文
      section: node.part_kind === 'format' ? 'tender_annex' : node.section || 'tender_main',
      stable_id: node.stable_id,
      children: toPayload(node.children),
    }));

interface TenderOutlineEditorProps {
  open: boolean;
  nodes: TenderOutlineNode[];
  /** 已有正文的 stable_id（删除时提示风险） */
  filledStableIds: Set<string>;
  saving: boolean;
  onClose: () => void;
  onSave: (payload: TenderOutlineEditPayload[]) => void;
}

const inputClass =
  'w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs outline-none focus:border-indigo-400';

/** 招标文件目录编辑器：增删改章节、调整层级与顺序、修改描述与字数 */
export default function TenderOutlineEditor({
  open,
  nodes,
  filledStableIds,
  saving,
  onClose,
  onSave,
}: TenderOutlineEditorProps) {
  const [tree, setTree] = useState<EditNode[]>([]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    title: '',
    description: '',
    word_count_range: '',
    part_kind: 'default' as TenderPartKind,
  });

  const prevOpenRef = useRef(false);
  useEffect(() => {
    // 仅在弹窗「打开」时用最新目录初始化；打开期间后台刷新（如批量编制完成）不打断编辑
    if (open && !prevOpenRef.current) {
      setTree(buildTree(nodes));
      setCollapsed(new Set());
      setEditingKey(null);
    }
    prevOpenRef.current = open;
  }, [open, nodes]);

  const total = useMemo(() => countNodes(tree), [tree]);

  const update = (mutate: (next: EditNode[]) => void) => {
    const next = cloneTree(tree);
    mutate(next);
    setTree(next);
  };

  const startEdit = (node: EditNode) => {
    setEditingKey(node.key);
    setDraft({
      title: node.title,
      description: node.description,
      word_count_range: node.word_count_range,
      part_kind: node.part_kind,
    });
  };

  const commitEdit = () => {
    if (!editingKey) {
      return;
    }
    update((next) => {
      const loc = locate(next, editingKey);
      if (!loc) {
        return;
      }
      const target = loc.list[loc.index];
      target.title = draft.title;
      target.description = draft.description;
      target.word_count_range = draft.word_count_range;
      target.part_kind = draft.part_kind;
    });
    setEditingKey(null);
  };

  const handleAddRoot = () => {
    update((next) => {
      next.push(makeNode({ title: '新章节' }));
    });
  };

  const handleAddChild = (key: string) => {
    update((next) => {
      const loc = locate(next, key);
      if (!loc) {
        return;
      }
      const parent = loc.list[loc.index];
      parent.children.push(makeNode({ title: '新子章节', section: parent.section }));
      setCollapsed((prev) => {
        const copy = new Set(prev);
        copy.delete(key);
        return copy;
      });
    });
  };

  const handleDelete = (key: string) => {
    const loc = locate(tree, key);
    const node = loc ? loc.list[loc.index] : null;
    const hasContent = !!node?.stable_id && filledStableIds.has(node.stable_id);
    const childCount = node ? countNodes(node.children) : 0;
    const tip = hasContent
      ? `「${node?.title}」已有正文，删除后该正文将不再关联（建议先用「版本」保存快照）。确定删除吗？`
      : childCount > 0
        ? `「${node?.title}」包含 ${childCount} 个子章节，确定一并删除吗？`
        : `确定删除「${node?.title}」吗？`;
    if (!window.confirm(tip)) {
      return;
    }
    update((next) => {
      const target = locate(next, key);
      if (target) {
        target.list.splice(target.index, 1);
      }
    });
  };

  const handleMove = (key: string, direction: -1 | 1) => {
    update((next) => {
      const loc = locate(next, key);
      if (!loc) {
        return;
      }
      const target = loc.index + direction;
      if (target < 0 || target >= loc.list.length) {
        return;
      }
      const [item] = loc.list.splice(loc.index, 1);
      loc.list.splice(target, 0, item);
    });
  };

  const handleIndent = (key: string) => {
    update((next) => {
      const loc = locate(next, key);
      if (!loc || loc.index === 0) {
        return;
      }
      const [item] = loc.list.splice(loc.index, 1);
      loc.list[loc.index - 1].children.push(item);
    });
  };

  const handleOutdent = (key: string) => {
    update((next) => {
      const loc = locate(next, key);
      if (!loc || !loc.parent) {
        return;
      }
      const parentLoc = locate(next, loc.parent.key);
      if (!parentLoc) {
        return;
      }
      const [item] = loc.list.splice(loc.index, 1);
      parentLoc.list.splice(parentLoc.index + 1, 0, item);
    });
  };

  const toggleCollapse = (key: string) => {
    setCollapsed((prev) => {
      const copy = new Set(prev);
      if (copy.has(key)) {
        copy.delete(key);
      } else {
        copy.add(key);
      }
      return copy;
    });
  };

  if (!open) {
    return null;
  }

  const renderTree = (items: EditNode[], level: number) =>
    items.map((node) => {
      const isEditing = editingKey === node.key;
      const isCollapsed = collapsed.has(node.key);
      const hasContent = !!node.stable_id && filledStableIds.has(node.stable_id);
      return (
        <div key={node.key}>
          <div
            className={`flex flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 px-2 py-1.5 ${
              level === 0 ? 'bg-white' : 'bg-gray-50/70'
            }`}
            style={{ marginLeft: `${level * 18}px` }}
          >
            {node.children.length > 0 ? (
              <button
                type="button"
                onClick={() => toggleCollapse(node.key)}
                className="rounded p-0.5 text-gray-400 hover:bg-gray-100"
                title={isCollapsed ? '展开子章节' : '收起子章节'}
              >
                {isCollapsed ? (
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDownIcon className="h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="w-4" />
            )}
            <span className="min-w-0 flex-1 truncate text-xs text-gray-800" title={node.title}>
              {node.title || '（未命名章节）'}
            </span>
            <span className="shrink-0 rounded-full border border-gray-200 bg-gray-50 px-1.5 text-[10px] text-gray-500">
              {PART_KIND_LABELS[node.part_kind] || '其他'}
            </span>
            {node.word_count_range && (
              <span className="shrink-0 text-[10px] text-gray-400">{node.word_count_range} 字</span>
            )}
            {hasContent && (
              <span
                className="shrink-0 text-[10px] text-emerald-600"
                title="该章节已有正文（编辑目录不会丢失）"
              >
                <DocumentTextIcon className="h-3.5 w-3.5" />
              </span>
            )}
            <span className="flex shrink-0 items-center gap-0.5">
              <button
                type="button"
                onClick={() => (isEditing ? setEditingKey(null) : startEdit(node))}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                title="编辑标题 / 描述 / 字数 / 分部"
              >
                {isEditing ? (
                  <ChevronUpIcon className="h-3.5 w-3.5" />
                ) : (
                  <PencilSquareIcon className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                type="button"
                onClick={() => handleAddChild(node.key)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                title="添加子章节"
              >
                <PlusIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleMove(node.key, -1)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                title="上移"
              >
                <ArrowUpIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleMove(node.key, 1)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                title="下移"
              >
                <ArrowDownIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleOutdent(node.key)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                title="降低层级（升为上一级）"
              >
                <ChevronLeftIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleIndent(node.key)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                title="提高层级（并入上一个章节）"
              >
                <ChevronRightIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(node.key)}
                className="rounded p-1 text-rose-500 hover:bg-rose-50"
                title="删除章节"
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          </div>

          {isEditing && (
            <div
              className="mt-1 space-y-2 rounded-lg border border-indigo-200 bg-indigo-50/50 p-2.5"
              style={{ marginLeft: `${level * 18 + 18}px` }}
            >
              <input
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                placeholder="章节标题（可含编号，如：第三章 评标办法）"
                className={inputClass}
              />
              <textarea
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                rows={2}
                placeholder="章节描述 / 编写要点（会作为编制该章的提示依据）"
                className={inputClass}
              />
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  value={draft.word_count_range}
                  onChange={(event) =>
                    setDraft({ ...draft, word_count_range: event.target.value })
                  }
                  placeholder="字数范围（如 800-1500）"
                  className={inputClass}
                />
                <select
                  value={draft.part_kind}
                  onChange={(event) =>
                    setDraft({ ...draft, part_kind: event.target.value as TenderPartKind })
                  }
                  className={inputClass}
                  title="分部类型决定本章的编制口径（公告 / 须知 / 评标 / 合同 / 需求 / 格式）"
                >
                  {Object.entries(PART_KIND_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={commitEdit}
                  className="rounded-lg bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                >
                  确定
                </button>
                <button
                  type="button"
                  onClick={() => setEditingKey(null)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 hover:bg-gray-50"
                >
                  取消
                </button>
              </div>
            </div>
          )}

          {!isCollapsed && node.children.length > 0 && (
            <div className="mt-1 space-y-1">{renderTree(node.children, level + 1)}</div>
          )}
        </div>
      );
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">编辑目录</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              共 {total} 个章节：可增删改章节、调整顺序与层级、修改描述与字数；章节编号（1 / 1.1 / 1.1.1）
              保存后由系统按顺序自动重排，已有正文按章节标识保留。
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
            title="关闭"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-1 overflow-y-auto px-4 py-3">{renderTree(tree, 0)}</div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-4 py-3">
          <button
            type="button"
            onClick={handleAddRoot}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            新增一级章节
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
            >
              取消
            </button>
            <button
              type="button"
              onClick={() => onSave(toPayload(tree))}
              disabled={saving || total === 0}
              className="rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {saving ? '保存中…' : '保存目录'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
